import { ActionMode, UserPermission } from '@/enums/scenario-builder.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  TReportTemplate,
  TReportTemplateDetails,
} from '@/interfaces/scenarioBuilder.interface'
import { userHasPermission } from '@/services/scenarioBuilder.service'
import {
  setActionMode,
  setSelectedReportTemplate,
  toggleDeleteReportTemplateModalOpened,
  toggleRunReportTemplateModalOpened,
} from '@/states/scenarioBuilder.slice'
import MenuIcon from '@mui/icons-material/Menu'
import { IconButton, Menu, MenuItem } from '@mui/material'
import React from 'react'
import { useDispatch } from 'react-redux'

export type IScenarioTemplateProps = {
  data: TReportTemplateDetails
}

export const ReportTemplateGridMenu: React.FC<IScenarioTemplateProps> = ({
  data,
}) => {
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const userInfo = useAppSelector((s) => s.scenarioBuilder.userInfo)
  // Handlers
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleMenuItemClick = (action: () => void) => () => {
    action()
    handleClose()
  }
  const handleDeleteScenario = (template: TReportTemplate) => {
    dispatch(setSelectedReportTemplate(template))
    dispatch(toggleDeleteReportTemplateModalOpened(true))
    dispatch(setActionMode(ActionMode.DELETE))
  }
  const handleUpdateScenario = (template: TReportTemplate) => {
    dispatch(setSelectedReportTemplate(template))
    dispatch(setActionMode(ActionMode.UPDATE))
  }
  const handleRunScenario = (template: TReportTemplate) => {
    dispatch(setSelectedReportTemplate(template))
    dispatch(toggleRunReportTemplateModalOpened(true))
  }

  // Variables
  const hasRunPermission = userHasPermission(
    userInfo,
    UserPermission.RUN_SCENARIO,
  )
  const hasSchedulePermission = userHasPermission(
    userInfo,
    UserPermission.SCHEDULE_SCENARIO,
  )
  const hasEditPermission = userHasPermission(
    userInfo,
    UserPermission.EDIT_REPORT_TEMPLATE,
  )
  const hasDeletePermission = userHasPermission(
    userInfo,
    UserPermission.DELETE_REPORT_TEMPLATE,
  )

  // Don't show hamburger when all permissions are missing
  if (
    !hasRunPermission &&
    !hasEditPermission &&
    !hasDeletePermission &&
    !hasSchedulePermission
  ) {
    return null
  }

  return (
    <>
      <IconButton
        id='action-button'
        data-testid='action-button'
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={(ref: any) => {
          if (!ref) return
          ref.onclick = (e: MouseEvent) => {
            e.stopPropagation()
            setAnchorEl(ref)
          }
        }}
      >
        <MenuIcon fontSize='small' sx={{ fontSize: '16px' }} />
      </IconButton>
      <Menu
        id='action-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
      >
        {(hasRunPermission || hasSchedulePermission) && (
          <MenuItem
            onClick={handleMenuItemClick(() => handleRunScenario(data))}
          >
            Create Report
          </MenuItem>
        )}
        {hasEditPermission && (
          <MenuItem
            onClick={handleMenuItemClick(() => handleUpdateScenario(data))}
          >
            Edit Template
          </MenuItem>
        )}
        {hasDeletePermission && (
          <MenuItem
            onClick={handleMenuItemClick(() => handleDeleteScenario(data))}
          >
            Delete Template
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
